import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import { PlusCircle, Image, MessageSquare, TrendingUp } from 'lucide-react';

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/my/listings').then(r => setMyListings(r.data)).catch(() => {}),
      api.get('/enquiries').then(r => setEnquiries(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const activeListings = myListings.filter(l => l.status === 'active').length;
  const draftListings = myListings.filter(l => l.status === 'draft').length;
  const unreadEnquiries = enquiries.filter(e => e.recipient_id === user?.id && !e.read).length;

  const stats = [
    { label: 'Total Listings', value: myListings.length, icon: Image, color: 'var(--text)' },
    { label: 'Active Listings', value: activeListings, icon: TrendingUp, color: 'var(--success)' },
    { label: 'Drafts', value: draftListings, icon: Image, color: 'var(--text-muted)' },
    { label: 'Unread Enquiries', value: unreadEnquiries, icon: MessageSquare, color: 'var(--gold)' },
  ];

  return (
    <DashboardShell title="Overview">
      <div className="space-y-8">
        {/* Welcome */}
        <div>
          <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Welcome back, {user?.name?.split(' ')[0]}</h2>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Here’s an overview of your activity.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(s => (
            <div key={s.label} className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
              <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
              <p className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-serif)', color: s.color }}>{loading ? '—' : s.value}</p>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-muted)' }}>Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate('/dashboard/listings/create')} style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '10px' }} data-testid="member-create-listing-button">
              <PlusCircle size={15} className="mr-2" /> Create Listing
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/listings')} style={{ borderColor: 'var(--border-strong)', borderRadius: '10px' }}>
              <Image size={15} className="mr-2" /> My Listings
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/enquiries')} style={{ borderColor: 'var(--border-strong)', borderRadius: '10px' }}>
              <MessageSquare size={15} className="mr-2" /> Enquiries {unreadEnquiries > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'var(--gold)', color: 'white' }}>{unreadEnquiries}</span>}
            </Button>
          </div>
        </div>

        {/* Recent listings */}
        {myListings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium" style={{ color: 'var(--text)' }}>Recent Listings</h3>
              <Link to="/dashboard/listings" className="text-xs" style={{ color: 'var(--gold)' }}>View all</Link>
            </div>
            <div className="space-y-3">
              {myListings.slice(0, 3).map(l => (
                <div key={l.id} className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{l.title}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{l.category_name} · {l.type_name}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ml-3 flex-shrink-0 status-${l.status}`}>{l.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
